import {
  TerritorialLevel,
  mapLevelNumberToString,
  type Country,
  type CountryDetail,
  type CountryHierarchy,
  type CountryPaginationParams,
  type CreateCountryRequest,
  type Paginated,
  type UpdateCountryRequest,
} from '../types/countries.types';
import type { IHttpResponse } from '@/core/api/HttpClient';
import httpClient from '@/core/api/createAxiosHttpClient';

/**
 * The API sends `level` as the numeric index it also expects on create (0 = Pais … 4 = Colonia),
 * while the app works with the `TerritorialLevel` string enum everywhere. Normalizing here keeps
 * that conversion in one place; a response that already carries the string passes through.
 */
const withLevelAsString = <T extends { level: unknown }>(item: T): T =>
  typeof item.level === 'number' ? { ...item, level: mapLevelNumberToString(item.level) } : item;

const withHierarchyLevelsAsString = (node: CountryHierarchy): CountryHierarchy => ({
  ...withLevelAsString(node),
  children: (node.children ?? []).map(withHierarchyLevelsAsString),
});

export const countriesApi = {
  getCountries: async (
    pagination: CountryPaginationParams = {},
  ): Promise<IHttpResponse<Paginated<Country>>> => {
    const { page = 1, pageSize = 10, searchTerm, isActive, level, parentId } = pagination;

    const levelIndex =
      level === TerritorialLevel.Pais
        ? 0
        : level === TerritorialLevel.Estado
          ? 1
          : level === TerritorialLevel.Municipio
            ? 2
            : level === TerritorialLevel.Localidad
              ? 3
              : undefined;

    const response = await httpClient.get<Paginated<Country>>('/admin/countries', {
      params: {
        Page: page,
        PageSize: pageSize,
        ...(searchTerm && { SearchTerm: searchTerm }),
        ...(isActive !== undefined && { IsActive: isActive }),
        ...(levelIndex !== undefined && { Level: levelIndex }),
        ...(parentId && { ParentId: parentId }),
      },
    });

    if (!response.data?.items) return response;

    return {
      ...response,
      data: { ...response.data, items: response.data.items.map(withLevelAsString) },
    };
  },

  createCountry: async (data: CreateCountryRequest): Promise<IHttpResponse<Country>> => {
    return httpClient.post<CreateCountryRequest, Country>('/admin/countries', data);
  },

  getCountryById: async (id: string): Promise<IHttpResponse<CountryDetail>> => {
    const response = await httpClient.get<CountryDetail>(`/admin/countries/${id}`);
    return response.data ? { ...response, data: withLevelAsString(response.data) } : response;
  },

  getCountryHierarchy: async (id: string): Promise<IHttpResponse<CountryHierarchy>> => {
    const response = await httpClient.get<CountryHierarchy>(`/admin/countries/${id}/hierarchy`);
    return response.data
      ? { ...response, data: withHierarchyLevelsAsString(response.data) }
      : response;
  },

  updateCountry: async (id: string, data: UpdateCountryRequest): Promise<IHttpResponse<void>> => {
    const response = await httpClient.put<UpdateCountryRequest>(`/admin/countries/${id}`, data);
    return { ...response, data: undefined as unknown as void };
  },

  toggleStatus: async (id: string, isActive: boolean): Promise<IHttpResponse<void>> => {
    const response = await httpClient.put<{ isActive: boolean }>(`/admin/countries/${id}/status`, {
      isActive,
    });
    return { ...response, data: undefined as unknown as void };
  },

  deleteCountry: async (id: string): Promise<IHttpResponse<void>> => {
    return httpClient.delete<void>(`/admin/countries/${id}`);
  },
};
